import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import Draggable from 'react-draggable'
import type { DraggableData, DraggableEvent } from 'react-draggable'

import { Modal as AntModal, notification, Col, Row, Input, Tooltip } from 'antd'
import styled from 'styled-components'

import { sizes, colours } from '@qflow/theme'

import { UTILITIES } from 'portal/components/Utilities/common/constants'
import { icons, Spacer, ColourButton } from 'portal/lib/primitives'
import { Meter } from './Interface'

type AddEditMeterModalProps = {
  defaultUtilityType: string
  meter: Meter | null
  isOpen: boolean
  onCancel: () => void
  onSubmit: (
    arg0: string,
    arg1: string,
    arg2: string,
    arg3: () => void,
    arg4: () => void
  ) => void
  onDelete: (arg0: string) => void
}

export function AddEditMeterModal({
  defaultUtilityType,
  meter = null,
  isOpen,
  onCancel,
  onSubmit,
  onDelete
}: AddEditMeterModalProps) {
  const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 })
  const [meterName, setMeterName] = useState('')
  const [meterReference, setMeterReference] = useState('')
  const [utilityType, setUtilityType] = useState('')
  const [meterReferenceAlreadyExists, setMeterReferenceAlreadyExists] =
    useState(false)
  const [meterNameMinimumLengthWarning, setMeterNameMinimumLengthWarning] =
    useState(false)
  const [
    meterReferenceMinimumLengthWarning,
    setMeterReferenceMinimumLengthWarning
  ] = useState(false)

  const allMeters = useSelector((state: any) => state.meters)
  // Split meters into water and electricity
  const { waterMeters, electricityMeters } = useMemo(() => {
    let waterMeters: Meter[] = []
    let electricityMeters: Meter[] = []

    if (allMeters.data && allMeters.data.length > 0) {
      const tempMeters = [...allMeters.data]
      tempMeters.sort((a: Meter, b: Meter) => {
        return a.meterName.localeCompare(b.meterName)
      })

      waterMeters = tempMeters.filter(
        (meter: Meter) =>
          meter.utilityType.toLowerCase() === UTILITIES.WATER.toLowerCase()
      )
      electricityMeters = tempMeters.filter(
        (meter: Meter) =>
          meter.utilityType.toLowerCase() ===
          UTILITIES.ELECTRICITY.toLowerCase()
      )
    }

    return { waterMeters, electricityMeters }
  }, [allMeters.data])

  const draggleRef = useRef<HTMLDivElement>(null)

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement
    const targetRect = draggleRef.current?.getBoundingClientRect()
    if (!targetRect) {
      return
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y)
    })
  }

  const showSuccessNotification = () => {
    notification.success({
      message: `Meter successfully ${meter ? 'updated' : 'added'}.`,
      duration: 5,
      placement: 'top'
    })
  }

  const showFailureNotification = () => {
    notification.error({
      message: `Meter failed to be ${meter ? 'updated' : 'added'}.`,
      description: 'Please check the values and try again.',
      duration: 10,
      placement: 'top'
    })
  }

  const modalTitle = useMemo(
    () =>
      `${meter ? (utilityType === UTILITIES.ELECTRICITY ? 'Edit electricity' : 'Edit water') : utilityType === UTILITIES.ELECTRICITY ? 'Add electricity' : 'Add water'} meter`,
    [meter, utilityType]
  )

  useEffect(() => {
    if (isOpen) {
      if (defaultUtilityType && !meter) {
        setMeterName('')
        setMeterReference('')
        setUtilityType(defaultUtilityType)
      }
      if (meter) {
        setMeterName(meter.meterName)
        setMeterReference(meter.meterReference)
        setUtilityType(meter.utilityType)
      }
    }
  }, [defaultUtilityType, isOpen, meter])

  const handleSubmit = () => {
    onSubmit(
      meterName,
      meterReference,
      utilityType,
      showSuccessNotification,
      showFailureNotification
    )
    setMeterName('')
    setMeterReference('')
    setMeterReferenceAlreadyExists(false)
  }

  const checkMeterReferenceAlreadyExists = useCallback(() => {
    // if updating an existing meter, allow the same meter reference
    if (meter && meter.meterReference === meterReference) {
      setMeterReferenceAlreadyExists(false)
      return false
    }

    if (utilityType === UTILITIES.ELECTRICITY) {
      if (
        electricityMeters.some(
          (m: Meter) => m.meterReference === meterReference
        )
      ) {
        setMeterReferenceAlreadyExists(true)
        return true
      }
    } else if (utilityType === UTILITIES.WATER) {
      if (waterMeters.some((m: Meter) => m.meterReference === meterReference)) {
        setMeterReferenceAlreadyExists(true)
        return true
      }
    }

    setMeterReferenceAlreadyExists(false)
    return false
  }, [meter, utilityType, electricityMeters, meterReference, waterMeters])

  const saveDisabled = useMemo(() => {
    return (
      !meterName ||
      !meterReference ||
      meterNameMinimumLengthWarning ||
      meterReferenceMinimumLengthWarning ||
      !utilityType ||
      checkMeterReferenceAlreadyExists()
    )
  }, [
    meterName,
    meterReference,
    meterNameMinimumLengthWarning,
    meterReferenceMinimumLengthWarning,
    utilityType,
    checkMeterReferenceAlreadyExists
  ])

  const handleCancel = () => {
    setMeterName('')
    setMeterReference('')
    setMeterReferenceAlreadyExists(false)
    onCancel()
  }

  const handleDelete = () => {
    if (meter) {
      onDelete(meter.meterId as string)
    }
    handleCancel()
  }

  return (
    <AntModal
      title={<StyledTitle>{modalTitle}</StyledTitle>}
      open={isOpen}
      centered
      closeIcon={<icons.Cross colour={colours.BLACK} size={sizes.SMALL} />}
      onCancel={handleCancel}
      maskClosable={false}
      footer={null}
      width={400}
      wrapClassName="meter-modal"
      modalRender={modal => (
        <Draggable
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
          cancel="#utilityTypeSelectWrapper, #meterReferenceInput, #meterNameInput"
        >
          <div ref={draggleRef}>{modal}</div>
        </Draggable>
      )}
      transitionName=""
    >
      <Row style={{ marginBottom: '10px' }}>
        <Col flex="auto">
          <StyledLabel htmlFor="meterReferenceInput">
            Meter reference
          </StyledLabel>
          <Input
            id="meterReferenceInput"
            value={meterReference}
            onChange={e => {
              setMeterReference(e.target.value)
              if (meterReferenceMinimumLengthWarning) {
                setMeterReferenceMinimumLengthWarning(
                  e.target.value.trim().length > 0 &&
                    e.target.value.trim().length < 3
                )
              }
            }}
            onBlur={e => {
              setMeterReference(e.target.value.trim())
              setMeterReferenceMinimumLengthWarning(
                e.target.value.trim().length > 0 &&
                  e.target.value.trim().length < 3
              )
            }}
            maxLength={200}
          />
          {meterReferenceAlreadyExists && (
            <ErrorContainer>
              <icons.CrossCircle
                size={sizes.SMALL_MEDIUM}
                colour={colours.RED}
              />
              <div>This meter reference is already in use</div>
            </ErrorContainer>
          )}
          {meterReferenceMinimumLengthWarning && (
            <ErrorContainer>
              <icons.CrossCircle
                size={sizes.SMALL_MEDIUM}
                colour={colours.RED}
              />
              <div>Meter reference must be 3+ characters</div>
            </ErrorContainer>
          )}
        </Col>
      </Row>
      <Row style={{ marginBottom: '10px' }}>
        <Col flex="auto">
          <StyledLabel htmlFor="meterNameInput">Meter name</StyledLabel>
          <Input
            id="meterNameInput"
            value={meterName}
            onChange={e => {
              setMeterName(e.target.value)
              if (meterNameMinimumLengthWarning) {
                setMeterNameMinimumLengthWarning(
                  e.target.value.trim().length > 0 &&
                    e.target.value.trim().length < 3
                )
              }
            }}
            onBlur={e => {
              setMeterName(e.target.value.trim())
              setMeterNameMinimumLengthWarning(
                e.target.value.trim().length > 0 &&
                  e.target.value.trim().length < 3
              )
            }}
            maxLength={200}
          />
          {meterNameMinimumLengthWarning && (
            <ErrorContainer>
              <icons.CrossCircle
                size={sizes.SMALL_MEDIUM}
                colour={colours.RED}
              />
              <div>Meter name must be 3+ characters</div>
            </ErrorContainer>
          )}
        </Col>
      </Row>
      {meter && (
        <Row style={{ justifyContent: 'space-between', marginTop: '1.5rem' }}>
          <Col>
            <Tooltip
              title={
                meter.latestReading
                  ? 'Cannot delete a meter that has one or more readings submitted.'
                  : null
              }
              placement="left"
            >
              <div>
                <ColourButton.BasicBorderless
                  onClick={handleDelete}
                  style={{
                    color: meter.latestReading
                      ? colours.BATTLESHIP_GREY
                      : colours.ERROR
                  }}
                  disabled={meter.latestReading ? true : false}
                >
                  Delete meter
                </ColourButton.BasicBorderless>
              </div>
            </Tooltip>
          </Col>
          <Col style={{ display: 'flex' }}>
            <ColourButton.Basic onClick={handleCancel}>
              Cancel
            </ColourButton.Basic>
            <Spacer.Tiny />
            <ColourButton.Action onClick={handleSubmit} disabled={saveDisabled}>
              Update meter
            </ColourButton.Action>
          </Col>
        </Row>
      )}
      {!meter && (
        <Row style={{ justifyContent: 'flex-end', marginTop: '1.5rem' }}>
          <Col>
            <ColourButton.Basic onClick={handleCancel}>
              Cancel
            </ColourButton.Basic>
          </Col>
          <Spacer.Tiny />
          <Col>
            <ColourButton.Action onClick={handleSubmit} disabled={saveDisabled}>
              Add meter
            </ColourButton.Action>
          </Col>
        </Row>
      )}
    </AntModal>
  )
}

const StyledTitle = styled.div`
  width: 100%;
  cursor: move;
`
const StyledLabel = styled.label`
  display: inline-block;
  margin-bottom: 0.5rem;
`
const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  color: ${colours.CORE_NAVY};
  background-color: ${colours.RED}11;
  border-radius: 5px;
  border: 1px solid ${colours.RED}22;
  padding: 0.25rem 1rem 0.25rem 0.75rem;
  margin-top: 0.5rem;
`
